(() => {
  if (customElements.get('vasenia-pattern-builder')) return;

  class VaseniaPatternBuilder extends HTMLElement {
    static quote(items, currency = 'USD') {
      if (!Array.isArray(items) || items.length > 4) throw new RangeError('Invalid selection size');
      const productIds = new Set();
      const variantIds = new Set();
      let subtotal = 0;
      for (const item of items) {
        const productId = String(item.productId);
        const variantId = String(item.id);
        if (!/^\d+$/.test(productId) || !/^\d+$/.test(variantId) || productIds.has(productId) || variantIds.has(variantId)) throw new TypeError('Invalid or duplicate pattern');
        if (!Number.isSafeInteger(item.price) || item.price < 0) throw new TypeError('Invalid price');
        productIds.add(productId);
        variantIds.add(variantId);
        subtotal += item.price;
      }
      if (!Number.isSafeInteger(subtotal)) throw new RangeError('Invalid subtotal');
      const count = items.length;
      const currencySupported = currency === 'USD';
      const savings = currencySupported ? ({ 2: 500, 3: 1000, 4: 1500 }[count] || 0) : null;
      const total = savings === null ? null : Math.max(0, subtotal - savings);
      return Object.freeze({ count, subtotal, savings, total, currency, currencySupported, eligible: currencySupported && count >= 2 && subtotal >= savings });
    }

    connectedCallback() {
      if (this.initialized) return;
      const configNode = this.querySelector('[data-builder-config]');
      if (!configNode) return;
      try { this.config = JSON.parse(configNode.textContent); } catch { return; }
      this.initialized = true;
      this.abortController = new AbortController();
      this.inputs = [...this.querySelectorAll('[data-pattern]')];
      this.submitButton = this.querySelector('[data-submit]');
      this.status = this.querySelector('[data-status]');
      this.busy = false;
      this.items = new Map();
      const products = new Set();
      for (const input of this.inputs) {
        const item = Object.freeze({ id: input.value, productId: input.dataset.productId, quantity: 1, price: Number(input.dataset.price), name: input.dataset.name || '' });
        const available = input.dataset.available === 'true';
        try {
          VaseniaPatternBuilder.quote([item], this.config.currency);
          if (products.has(item.productId) || this.items.has(item.id)) throw new TypeError('Duplicate card');
          products.add(item.productId);
          this.items.set(item.id, item);
          input.dataset.valid = String(available);
        } catch {
          input.dataset.valid = 'false';
          input.checked = false;
        }
      }
      const listenerOptions = { signal: this.abortController.signal };
      this.addEventListener('change', event => this.onChange(event), listenerOptions);
      this.submitButton.addEventListener('click', () => this.submit(), listenerOptions);
      this.addEventListener('vasenia:builder:result', event => {
        this.busy = false;
        this.setAttribute('aria-busy', 'false');
        this.status.textContent = typeof event.detail?.message === 'string' ? event.detail.message : '';
        this.update();
      }, listenerOptions);
      this.querySelectorAll('[data-pattern]:checked').forEach((input, index) => {
        if (index >= 4 || input.dataset.valid !== 'true') input.checked = false;
      });
      this.update();
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.initialized = false;
    }

    setBusy(busy) {
      this.busy = Boolean(busy);
      this.setAttribute('aria-busy', String(this.busy));
      this.update();
    }

    setStatus(message, isError = false) {
      this.status.textContent = String(message || '');
      this.status.setAttribute('role', isError ? 'alert' : 'status');
      this.querySelector('[data-review-cart]').hidden = !isError;
    }

    selectedItems() {
      return this.inputs.filter(input => input.checked && input.dataset.valid === 'true').map(input => this.items.get(input.value));
    }

    money(cents) {
      if (cents === null) return '—';
      try {
        return new Intl.NumberFormat(this.config.locale || 'en', { style: 'currency', currency: this.config.currency }).format(cents / 100);
      } catch {
        return `${this.config.currency} ${(cents / 100).toFixed(2)}`;
      }
    }

    onChange(event) {
      if (!event.target.matches('[data-pattern]')) return;
      if (this.busy || event.target.dataset.valid !== 'true') event.target.checked = false;
      if (this.selectedItems().length > 4) event.target.checked = false;
      this.setStatus('');
      this.update(true);
    }

    update(announce = false) {
      const items = this.selectedItems();
      const quote = VaseniaPatternBuilder.quote(items, this.config.currency);
      const strings = this.config.strings;
      const selectedText = quote.count === 1 ? strings.oneSelected : strings.selectedCount.replace('[count]', quote.count);
      for (const input of this.inputs) {
        input.disabled = this.busy || input.dataset.valid !== 'true' || (quote.count === 4 && !input.checked);
        const card = input.closest('[data-card]');
        card.classList.toggle('is-selected', input.checked);
        card.classList.toggle('is-disabled', input.disabled);
        card.querySelector('[data-card-state]').textContent = input.checked ? strings.selected : (input.dataset.valid === 'true' ? strings.select : strings.unavailable);
      }
      this.querySelectorAll('[data-tier]').forEach(tier => tier.classList.toggle('is-active', Number(tier.dataset.tier) === quote.count));
      this.querySelector('[data-count]').textContent = quote.count ? selectedText : strings.chooseTwo;
      this.querySelector('[data-subtotal]').textContent = this.money(quote.subtotal);
      this.querySelector('[data-savings]').textContent = quote.savings ? `−${this.money(quote.savings)}` : this.money(quote.savings);
      this.querySelector('[data-total]').textContent = this.money(quote.total);
      let hint = quote.count === 0 ? strings.chooseTwoHint : strings.chooseOneHint;
      if (quote.count >= 2) hint = strings.savingHint.replace('[amount]', this.money(quote.savings));
      if (quote.count === 4) hint += ` ${strings.maximumHint}`;
      if (!quote.currencySupported) hint = strings.currencyNotice;
      this.querySelector('[data-hint]').textContent = hint;
      const list = this.querySelector('[data-selected-list]');
      list.replaceChildren(...items.map(item => {
        const li = document.createElement('li');
        const name = document.createElement('span');
        const price = document.createElement('span');
        name.textContent = item.name;
        price.textContent = this.money(item.price);
        li.append(name, price);
        return li;
      }));
      this.querySelector('[data-empty]').hidden = quote.count > 0;
      const configuredCode = String(this.config.discountCodes?.[quote.count] || '').trim();
      const checkoutEnabled = this.config.enableCheckout === true;
      this.submitButton.disabled = this.busy || !quote.eligible || !checkoutEnabled || !configuredCode;
      this.submitButton.textContent = this.busy ? strings.processing : (quote.count === 0 ? strings.chooseTwo : (quote.count === 1 ? strings.chooseOne : (checkoutEnabled ? strings.addToCart : strings.previewOnly)));
      this.querySelector('[data-mode-note]').textContent = !quote.currencySupported ? strings.currencyNotice : (!checkoutEnabled ? strings.previewNote : (!configuredCode && quote.count >= 2 ? strings.integrationUnavailable : ''));
      if (announce) this.status.textContent = `${selectedText}. ${hint}`;
      this.dispatchEvent(new CustomEvent('vasenia:builder:change', { bubbles: true, detail: Object.freeze({ ...quote, items: Object.freeze([...items]), sectionId: this.dataset.sectionId }) }));
    }

    submit() {
      if (this.busy || this.submitButton.disabled) return;
      const items = this.selectedItems();
      const quote = VaseniaPatternBuilder.quote(items, this.config.currency);
      const discountCode = String(this.config.discountCodes?.[quote.count] || '').trim();
      if (!quote.eligible || this.config.enableCheckout !== true || !discountCode) return;
      this.busy = true;
      this.setAttribute('aria-busy', 'true');
      this.status.textContent = this.config.strings.processing;
      this.update();
      const request = new CustomEvent('vasenia:builder:submit', {
        bubbles: true,
        cancelable: true,
        detail: Object.freeze({ ...quote, items: Object.freeze([...items]), discountCode, sectionId: this.dataset.sectionId })
      });
      if (this.dispatchEvent(request)) {
        this.busy = false;
        this.setAttribute('aria-busy', 'false');
        this.update();
        this.setStatus(this.config.strings.integrationUnavailable, true);
      }
    }
  }

  customElements.define('vasenia-pattern-builder', VaseniaPatternBuilder);
})();
